import { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney, formatQty } from '../lib/format.js'

// Reasons are kept short on purpose — long lists slow the kitchen flow.
// "Other" is the escape hatch. Add to seed/settings later if the chef wants more.
// Note: "Burnt" matters for a smoker — off-spec briskets are a real reason on
// a BBQ line. "Drop" was rare in practice and folds cleanly into "Other".
const REASONS = ['Spoilage', 'Over-prep', 'Trim', 'Customer return', 'Burnt', 'Other']

// Bottom-sheet entry form. Mounts on demand so we don't query Supabase on close.
export default function WasteEntryForm({
  open,
  onClose,
  onLogged,
  items,
  largeEntryThreshold = 50,
}) {
  const [query, setQuery] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState(REASONS[0])
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const searchRef = useRef(null)
  const qtyRef = useRef(null)

  // Reset state on every open so a new log starts clean.
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedItem(null)
      setQuantity('')
      setReason(REASONS[0])
      setNotes('')
      setError(null)
      // Defensive: if a prior save stuck submitting=true (the bug fixed
      // below), this also clears it on next open so the form unlocks.
      setSubmitting(false)
      // Focus the search box so the manager can start typing immediately.
      setTimeout(() => searchRef.current?.focus(), 100)
    }
  }, [open])

  // Filter items by query. Cap at 8 results so the list never overflows on a phone.
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items.slice(0, 8)
    return items
      .filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.category || '').toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [items, query])

  // LIVE dollar impact — recalculates on every keystroke per acceptance criterion #4.
  const liveCost = useMemo(() => {
    const q = Number(quantity)
    const c = Number(selectedItem?.cost_per_unit)
    if (!isFinite(q) || !isFinite(c)) return 0
    return Math.max(0, q * c)
  }, [quantity, selectedItem])

  const isLargeEntry = liveCost > Number(largeEntryThreshold)
  const canSubmit =
    selectedItem && Number(quantity) > 0 && !submitting

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const total = Number(liveCost.toFixed(2))
      const payload = {
        item_id: selectedItem.id,
        item_name: selectedItem.name,
        quantity: Number(quantity),
        unit: selectedItem.unit,
        // Snapshot cost-per-unit at log-time per acceptance criterion #9 —
        // so future cost changes don't rewrite history.
        cost_per_unit_at_log: selectedItem.cost_per_unit,
        total_cost: total,
        reason,
        notes: notes.trim() ? notes.trim() : null,
      }
      const { error: insertErr } = await supabase.from('waste_logs').insert(payload)
      if (insertErr) throw insertErr
      onLogged?.({ total })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[WasteEntryForm] insert failed', err)
      setError('Could not log waste. Check your connection and try again.')
    } finally {
      // Always reset — without finally, `submitting` stays true after a
      // successful log and the form locks until tab-switch or reload.
      // Same pattern bug as the modals (StaffEditModal, etc.).
      setSubmitting(false)
    }
  }

  function pickItem(it) {
    setSelectedItem(it)
    setQuery(it.name)
    // Auto-jump to quantity — the manager almost always wants to type a number next.
    setTimeout(() => qtyRef.current?.focus(), 50)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      {/* Sheet */}
      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Mark waste"
      >
        {/* Grab handle + sticky error banner */}
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Mark Waste</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-5 px-4 pb-4">
          {/* Item search */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="waste-item">
              Item
            </label>
            <input
              id="waste-item"
              ref={searchRef}
              type="text"
              inputMode="search"
              autoComplete="off"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                if (selectedItem && e.target.value !== selectedItem.name) {
                  setSelectedItem(null)
                }
              }}
              placeholder="Search items..."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-lg outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />

            {/* Typeahead results */}
            {!selectedItem && filteredItems.length > 0 && (
              <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-black/10">
                {filteredItems.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => pickItem(it)}
                      className="flex w-full items-center justify-between border-b border-black/5 px-4 py-3 text-left last:border-b-0 hover:bg-blue-50"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-base font-medium">
                          {it.name}
                        </div>
                        {it.category && (
                          <div className="truncate text-xs text-black/60">
                            {it.category}
                          </div>
                        )}
                      </div>
                      <div className="ml-3 text-right text-sm tabular-nums text-black/70">
                        {formatMoney(it.cost_per_unit)}/{it.unit}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!selectedItem && query.trim() && filteredItems.length === 0 && (
              <p className="mt-2 text-sm text-black/60">
                No matching items. Ask an admin to add it to the catalog.
              </p>
            )}

            {selectedItem && (
              <div className="mt-2 flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2 text-sm">
                <span className="font-medium">
                  {selectedItem.name}
                  <span className="ml-2 text-black/60">
                    {formatMoney(selectedItem.cost_per_unit)}/{selectedItem.unit}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedItem(null)
                    setQuery('')
                    setTimeout(() => searchRef.current?.focus(), 50)
                  }}
                  className="text-blue-600 underline"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="waste-qty">
              Quantity {selectedItem ? `(${selectedItem.unit})` : ''}
            </label>
            <input
              id="waste-qty"
              ref={qtyRef}
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-2xl font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            {/* Live impact */}
            <div className="mt-2 flex items-center justify-between rounded-lg bg-black/5 px-3 py-2">
              <span className="text-sm text-black/70">Estimated cost</span>
              <span className="text-xl font-medium tabular-nums">
                {formatMoney(liveCost)}
              </span>
            </div>
          </div>

          {/* >$50 warning banner */}
          {isLargeEntry && (
            <div className="rounded-lg border border-amber-brand-400 bg-amber-brand-50 px-3 py-3 text-sm font-medium text-amber-brand-800">
              Single entry over {formatMoney(largeEntryThreshold)} — owner will be notified.
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="waste-reason">
              Reason
            </label>
            <select
              id="waste-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Notes (optional) */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="waste-notes">
              Notes (optional)
            </label>
            <textarea
              id="waste-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. walk-in #2 was warm overnight"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Error display moved to the sticky header above so it's
              always visible regardless of scroll position. */}
        </div>

        {/* Sticky submit */}
        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-2xl font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Logging…'
              : `Log Waste${liveCost > 0 ? ` · ${formatMoney(liveCost)}` : ''}`}
          </button>
        </div>
      </form>
    </div>
  )
}
